import apiClient from './apiClient';
import {
  QuizResultRow,
  RankedLeaderboard,
  RankedProfile,
  RankedRosterResponse,
  RatingHistoryEvent
} from '../types/ranked';

/**
 * A student's tier, rating and — when the caller has full visibility — XP,
 * streak, rating deviation and peak rating.
 *
 * The response is a union discriminated on `visibility`; callers must narrow
 * before reading the private fields.
 */
export const getRankedProfile = async (userId: number): Promise<RankedProfile> => {
  const response = await apiClient.get(`/api/ranked/${userId}/profile`);
  return response.data;
};

/** Season leaderboard, ordered by rating with ties sharing a rank. */
export const getRankedLeaderboard = async (
  page = 1,
  limit = 25
): Promise<RankedLeaderboard> => {
  const response = await apiClient.get('/api/ranked/leaderboard', {
    params: { page, limit }
  });
  return response.data;
};

/**
 * Recent rating changes, newest first. Backs the delta arrows on the profile.
 *
 * Rated quizzes only — use getQuizResults for everything the student played.
 * Requires full visibility on the student.
 */
export const getRatingHistory = async (
  userId: number,
  limit?: number
): Promise<RatingHistoryEvent[]> => {
  const response = await apiClient.get(`/api/ranked/${userId}/history`, {
    params: limit ? { limit } : undefined
  });
  return response.data.events;
};

/**
 * Every quiz the student took — live and self-paced, rated and unrated.
 * Requires full visibility on the student.
 */
export const getQuizResults = async (
  userId: number,
  limit?: number
): Promise<QuizResultRow[]> => {
  const response = await apiClient.get(`/api/ranked/${userId}/quiz-results`, {
    params: limit ? { limit } : undefined
  });
  return response.data.results;
};

/**
 * Ranked standing for a whole roster in one request — a parent's children or a
 * teacher's students.
 *
 * An empty roster short-circuits without issuing a request: a parent with no
 * linked children must not fire `?userIds=`, which the server rejects as a 400
 * and which would surface as a spurious error on an otherwise fine page.
 */
export const getRankedRoster = async (userIds: number[]): Promise<RankedRosterResponse> => {
  if (userIds.length === 0) {
    return {
      season: { id: 0, name: '', endsAt: '' },
      totalRanked: 0,
      entries: []
    };
  }

  const response = await apiClient.get('/api/ranked/roster', {
    params: { userIds: userIds.join(',') }
  });
  return response.data;
};
